import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImageIfNeeded } from '@/lib/imageCompress';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_UPLOAD_MB = 15;

/**
 * Combined image URL input + file upload button.
 * - Manual URL entry works (backwards compatible with legacy /images/menu paths).
 * - "Yükle" button opens a file picker, uploads to /api/admin/upload-image,
 *   and puts the returned URL into the same field.
 */
const ImageUploadField = ({ value, onChange, token: providedToken, testIdPrefix = 'image' }) => {
  const token = providedToken || (typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const original = e.target.files?.[0];
    if (!original) return;
    try {
      setUploading(true);
      // Client-side compression for big files (typical phone photos are 5-15MB)
      const compressed = await compressImageIfNeeded(original);
      const finalFile = compressed;
      if (finalFile.size > MAX_UPLOAD_MB * 1024 * 1024) {
        toast.error(`Dosya çok büyük (max ${MAX_UPLOAD_MB}MB)`);
        return;
      }
      if (compressed !== original) {
        const savedMb = ((original.size - compressed.size) / (1024 * 1024)).toFixed(1);
        toast.info(`Görsel sıkıştırıldı (${savedMb}MB tasarruf)`);
      }

      const fd = new FormData();
      fd.append('file', finalFile);
      const res = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed: ${res.status}`);
      }
      const data = await res.json();
      const absolute = data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
      onChange(absolute);
      toast.success('Görsel yüklendi');
    } catch (err) {
      toast.error(err.message || 'Yükleme başarısız');
      console.error(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/images/menu/... veya https://..."
          data-testid={`${testIdPrefix}-url-input`}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={handleFile}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading || !token}
          onClick={() => fileRef.current?.click()}
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {uploading ? 'Yükleniyor…' : 'Yükle'}
        </Button>
      </div>
      {value && (
        <img
          src={value}
          alt="Preview"
          className="w-full max-h-64 object-contain rounded-lg border border-border/40 bg-charcoal-light/30"
          onError={(e) => { if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) e.currentTarget.src = '/images/menu/placeholder.webp'; }}
        />
      )}
    </div>
  );
};

export default ImageUploadField;
